/**
 * html-to-image wraps the capture in an outer SVG foreignObject and serializes to a data
 * URL; nested chart foreignObject HTML often rasterizes with default (black) text even when
 * inline color is set. Axis uses SVG <text> and is fine.
 *
 * Export-only mitigations (restored after capture):
 * 1) Inject SVG <style> into each descendant <svg> so rules apply during XML serialization.
 * 2) Inline color / -webkit-text-fill-color with !important on foreignObject divs (backup).
 */

/** Cap total bitmap pixels (width×height×pixelRatio²); lower if export OOMs. */
export const CHRONOS_EXPORT_MAX_BITMAP_PIXELS = 900_000_000;

/** Upper bound for html-to-image `pixelRatio` (4× = sharper zoomed labels). */
export const CHRONOS_EXPORT_MAX_PIXEL_RATIO = 4;

/**
 * Browser canvas limits (GPU-dependent). Exceeding these with skipAutoScale:true yields
 * "Canvas is already in error state" on drawImage. Many engines allow ~32k per edge; area
 * caps vary — use conservative values; fractional pixelRatio uses the headroom fully.
 */
export const CHRONOS_EXPORT_MAX_CANVAS_EDGE = 32767;
/** ~1e9 pixels — aligns with typical large-canvas budgets; lower if OOM. */
export const CHRONOS_EXPORT_MAX_CANVAS_AREA = 1_000_000_000;

/**
 * Highest pixelRatio (possibly fractional) within memory budget and canvas limits.
 * Integer-only caps wasted a lot of range on wide exports (e.g. 32k CSS px → pr stuck at 1).
 */
export function computeChronosExportPixelRatio(
  width,
  height,
  {
    maxPixels = CHRONOS_EXPORT_MAX_BITMAP_PIXELS,
    maxRatio = CHRONOS_EXPORT_MAX_PIXEL_RATIO,
  } = {}
) {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  let prInt = Math.max(
    1,
    Math.min(
      CHRONOS_EXPORT_MAX_PIXEL_RATIO,
      Math.floor(Number(maxRatio)) || CHRONOS_EXPORT_MAX_PIXEL_RATIO
    )
  );
  while (prInt > 1 && w * h * prInt * prInt > maxPixels) {
    prInt -= 1;
  }
  const maxEdge = CHRONOS_EXPORT_MAX_CANVAS_EDGE;
  const maxArea = CHRONOS_EXPORT_MAX_CANVAS_AREA;
  const byEdgeW = maxEdge / w;
  const byEdgeH = maxEdge / h;
  const byArea = Math.sqrt(maxArea / (w * h));
  const hardwareCap = Math.min(byEdgeW, byEdgeH, byArea);
  let pr = Math.min(prInt, hardwareCap, CHRONOS_EXPORT_MAX_PIXEL_RATIO);
  if (!Number.isFinite(pr) || pr < 0.25) {
    pr = 0.25;
  }
  return Math.round(pr * 10000) / 10000;
}

const SVG_NS = "http://www.w3.org/2000/svg";

/** Muted date lines use fontSize 11 in ChronosTimelineView (see inline styles). */
const EXPORT_FO_STYLE_TEXT = `
foreignObject div {
  font-family: Inter, sans-serif !important;
  color: rgba(255, 255, 255, 0.94) !important;
  -webkit-text-fill-color: rgba(255, 255, 255, 0.94) !important;
}
foreignObject div[style*="font-size:11px"], foreignObject div[style*="font-size: 11px"] {
  font-family: Inter, sans-serif !important;
  color: rgba(255, 255, 255, 0.68) !important;
  -webkit-text-fill-color: rgba(255, 255, 255, 0.68) !important;
}
`.trim();

export function applyChronosExportForeignObjectTextFix(containerEl) {
  if (!containerEl?.querySelectorAll) {
    return () => {};
  }

  const cleanups = [];

  const snapshots = [];
  containerEl.querySelectorAll("foreignObject div").forEach((div) => {
    snapshots.push({
      el: div,
      styleAttr: div.getAttribute("style"),
    });
    const cs = window.getComputedStyle(div);
    // html-to-image (and/or dark-mode extensions) may call string ops on computed font;
    // ensure font-family / font exist so serialization doesn't end up with `undefined.trim()`.
    const fontFamily = cs.fontFamily || "Inter, sans-serif";
    const font = cs.font || ""; // usually present; empty string is ok
    div.style.setProperty("font-family", fontFamily, "important");
    if (typeof font === "string" && font.trim() !== "") {
      div.style.setProperty("font", font, "important");
    }
    const fs = parseFloat(cs.fontSize) || 13;
    const muted = fs <= 11.5;
    const color = muted ? "rgba(255,255,255,0.68)" : "rgba(255,255,255,0.94)";
    div.style.setProperty("color", color, "important");
    div.style.setProperty("-webkit-text-fill-color", color, "important");
  });
  cleanups.push(() => {
    snapshots.forEach(({ el, styleAttr }) => {
      if (styleAttr == null || styleAttr === "") {
        el.removeAttribute("style");
      } else {
        el.setAttribute("style", styleAttr);
      }
    });
  });

  containerEl.querySelectorAll("svg").forEach((svg) => {
    const styleEl = document.createElementNS(SVG_NS, "style");
    styleEl.setAttribute("type", "text/css");
    styleEl.textContent = EXPORT_FO_STYLE_TEXT;
    svg.insertBefore(styleEl, svg.firstChild);
    cleanups.push(() => styleEl.remove());
  });

  return () => {
    for (let i = cleanups.length - 1; i >= 0; i -= 1) {
      cleanups[i]();
    }
  };
}
